import React, { useEffect, useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    api
      .get(`/auth/reset-password/verify`, { params: { token } })
      .then((r) => {
        setValidToken(true);
        setMeta(r.data);
      })
      .catch(() => setValidToken(false))
      .finally(() => setVerifying(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated");
      nav(`/login?reset=1`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteLayout>
      <section className="mx-auto max-w-md px-6 py-16">
        <span className="overline">Choose a new password</span>
        <h1 className="mt-2 font-display text-4xl font-bold text-[#0F2540]">Reset password</h1>

        {verifying && <p className="mt-6 text-sm text-[#5C6B7A]">Verifying your link…</p>}

        {!verifying && !validToken && (
          <div className="mt-6 rounded-2xl bg-[#FFF1EE] border border-[#F4C9C0] p-6" data-testid="reset-invalid">
            <h2 className="font-display text-xl font-bold text-[#A53A2A]">Link invalid or expired</h2>
            <p className="mt-2 text-sm text-[#0F2540]">
              This reset link is no longer valid. Reset links expire 30 minutes after being sent.
            </p>
            <Link to="/forgot-password" className="mt-4 btn-primary inline-flex">Request a new link</Link>
          </div>
        )}

        {!verifying && validToken && (
          <>
            <p className="mt-2 text-sm text-[#5C6B7A]">
              Setting a new password for <b>{meta?.email}</b>.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
              <div>
                <label className="text-sm font-medium text-[#0F2540]">New password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                  data-testid="reset-password" autoFocus
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F2540]">Confirm new password</label>
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6}
                  className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                  data-testid="reset-confirm"
                />
              </div>
              <button disabled={loading} className="btn-primary w-full justify-center" data-testid="reset-submit">
                {loading ? "Saving…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </section>
    </SiteLayout>
  );
}
